import type {
  EFeatureFlag,
  EFeatureFlagPlatform,
  IFeatureFlagDefinition,
} from "../constants/feature-flags";
import {
  EFeatureFlagOverrideMode,
  FEATURE_FLAG_LIST,
} from "../constants/feature-flags";
import type { IAppConfigResponse } from "../types/app-config";
import type { IStorageAdapter } from "./storage";

// NOTE: NSAI-1249 — the resolution half of the feature-flag registry in
// @frontend-shared/constants/feature-flags. Pure functions plus a thin store
// over IStorageAdapter, so web (localStorage) and mobile (AsyncStorage) share
// one implementation instead of re-deriving the same three-way rule twice.

// NOTE: The exact string an opt-in is stored as. Compared strictly, so a
// truthy-looking leftover ("1", "yes", "false") does not open a feature.
export const FEATURE_FLAG_ENABLED_VALUE = "true";

export type TFeatureFlagOverrides = Partial<Record<EFeatureFlag, boolean>>;

export interface IResolveFeatureFlagProps {
  definition: IFeatureFlagDefinition;
  isOptedInLocally: boolean;
  // NOTE: undefined while /app-config is still in flight. Every remote check
  // below then reads as "not enabled", which is the safe answer: a feature
  // flickering on after the response lands is better than one shown to a
  // deployment that never enabled it.
  appConfig?: IAppConfigResponse;
}

// NOTE: `remoteEnabled || (optedIn && overrideAllowed)` — the rule web has
// implemented by hand in each of its gate hooks since NSAI-5286, generalised.
// Ordered so the server's everyone-switch is decided first: once a feature has
// shipped for real, no stale device opt-in can turn it back off.
export const resolveFeatureFlag = ({
  definition,
  appConfig,
  isOptedInLocally,
}: IResolveFeatureFlagProps): boolean => {
  const { remoteEnabledKey, remoteFFKey, overrideMode } = definition;

  if (remoteEnabledKey && appConfig?.[remoteEnabledKey] === true) {
    return true;
  }

  if (!isOptedInLocally) {
    return false;
  }

  if (overrideMode === EFeatureFlagOverrideMode.ALWAYS) {
    return true;
  }

  // NOTE: fails closed on purpose — a REQUIRES_REMOTE_FF flag that names no
  // `remoteFFKey` is a registry mistake, and the safe reading of it is "the
  // server never allowed this".
  return Boolean(remoteFFKey && appConfig?.[remoteFFKey] === true);
};

export interface IIsFeatureFlagOverridableProps {
  definition: IFeatureFlagDefinition;
  appConfig?: IAppConfigResponse;
}

// NOTE: Whether flipping the device opt-in would change anything right now.
// The dev screen uses it to explain an inert toggle instead of letting a
// developer flip a switch and watch nothing happen.
export const isFeatureFlagOverridable = ({
  definition,
  appConfig,
}: IIsFeatureFlagOverridableProps): boolean => {
  const { remoteEnabledKey, remoteFFKey, overrideMode } = definition;

  if (remoteEnabledKey && appConfig?.[remoteEnabledKey] === true) {
    return false;
  }

  if (overrideMode === EFeatureFlagOverrideMode.ALWAYS) {
    return true;
  }

  return Boolean(remoteFFKey && appConfig?.[remoteFFKey] === true);
};

export const getFeatureFlagsForPlatform = (
  platform: EFeatureFlagPlatform,
): readonly IFeatureFlagDefinition[] =>
  FEATURE_FLAG_LIST.filter((definition) =>
    definition.platforms.includes(platform),
  );

export interface ISetFeatureFlagOverrideProps {
  definition: IFeatureFlagDefinition;
  isOptedIn: boolean;
}

// NOTE: Reads and writes the per-device opt-ins through the host app's storage
// adapter. Both adapters are synchronous — web wraps localStorage, mobile wraps
// an in-memory cache warmed from AsyncStorage at launch (see
// mobile/src/utils/storage.ts) — so a flag can be resolved during render without
// a loading state.
//
// Synchronous is not the same as ready, though, and the difference bit once:
// mobile's cache is EMPTY until `storageCacheReady` resolves, so a read taken at
// launch answers "no opt-in" for every key. A caller that reads once at mount
// has to re-read when hydration settles or the device's flags silently do not
// survive an app restart — see FeatureFlagProvider. Web has no such window.
export class FeatureFlagStore {
  private adapter: IStorageAdapter;

  constructor(adapter: IStorageAdapter) {
    this.adapter = adapter;
  }

  isOptedIn(definition: IFeatureFlagDefinition): boolean {
    return (
      this.adapter.getItem(definition.storageKey) === FEATURE_FLAG_ENABLED_VALUE
    );
  }

  setOptedIn({ definition, isOptedIn }: ISetFeatureFlagOverrideProps): void {
    if (isOptedIn) {
      this.adapter.setItem(definition.storageKey, FEATURE_FLAG_ENABLED_VALUE);
      return;
    }

    // NOTE: removed rather than written as "false" — an absent key is what
    // every existing web reader already treats as "not opted in", and it keeps
    // a device that never touched the dev screen indistinguishable from one
    // that opted back out.
    this.adapter.removeItem(definition.storageKey);
  }

  getOverrides(): TFeatureFlagOverrides {
    return FEATURE_FLAG_LIST.reduce<TFeatureFlagOverrides>(
      (overrides, definition) => ({
        ...overrides,
        [definition.key]: this.isOptedIn(definition),
      }),
      {},
    );
  }

  reset(): void {
    FEATURE_FLAG_LIST.forEach((definition) => {
      this.adapter.removeItem(definition.storageKey);
    });
  }
}
